/**
 * Grades — how everyone in the academy is doing, then one course in detail.
 *
 * NOT one course at a time, which is where this page started and why it was wrong: an academy
 * with a hundred courses opened it to a dropdown and an empty page, so "who is close to
 * passing?" began with guessing which course to look in.
 *
 * THE ACADEMY-WIDE LIST is ONE request to `academy/grades`, paged by enrolment rather than fanned
 * out one request per course — it replaces 25 calls (17 of them the same endpoint,
 * `/api/grades/v1/section_grades_breakdown/`, one per course) that were capped at `MAX_COURSES =
 * 24`, so a 25th course silently had no grades at all. It carries `passed` outright, so the table
 * knows whether 40% is a pass without a second request per course for the grading policy, and the
 * cutoff it applies is the course's own rather than one we reinvented.
 *
 * There is no email column, matching the endpoint this replaced: neither the per-course
 * breakdown nor the gradebook ever carried one.
 *
 * ONE COURSE IN DETAIL keeps what the per-course page was good at, and it is the reason the
 * drill-in exists rather than being a filtered table: the gradebook's `section_breakdown` gives
 * a column per assignment, `grading-info` gives the policy those columns are weighted by, and
 * a grade report is what an academy actually wants when the table is too long to read.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Banner,
  Button,
  DataTable,
  Kpi,
  KpiRow,
  Pill,
  SearchField,
  Spinner,
  Tabs,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { listAcademyGrades, type AcademyCourse, type AcademyGrade } from '../../shared/lib/academy';
import {
  asPercent,
  generateReport,
  getGradingInfo,
  listGradebook,
  listReports,
  reportDownloadUrl,
  type GradebookRow,
} from '../../shared/lib/instructor';
import { ApiError } from '../../shared/lib/openedx';
import { PageHeader } from '../AdminShell';
import { CourseCell, CourseSwitcher, courseChoices, useCourseParam } from '../CourseFilter';
import { useAcademyCourses } from '../useAcademyCourses';

type Band = 'all' | 'passing' | 'not-passing';

export default function AdminGrades() {
  const [course, setCourse] = useCourseParam();
  const [band, setBand] = useState<Band>('all');
  const [search, setSearch] = useState('');

  const courses = useAcademyCourses();

  const byKey = useMemo(() => {
    const map = new Map<string, AcademyCourse>();
    for (const entry of courses.all) map.set(entry.course_key, entry);
    return map;
  }, [courses.all]);

  // Independent of the course list — the endpoint is scoped by the tenant on the server, not by a
  // list of keys this app assembled — so the key is stable and the query never refetches because
  // the course list changed shape.
  const grades = useQuery({
    queryKey: ['academy-grades'],
    queryFn: () => listAcademyGrades({ pageSize: 200 }),
    staleTime: 60_000,
  });

  const all = grades.data?.results ?? [];
  const passing = all.filter((row) => row.passed);

  /**
   * The courses worth offering HERE: the ones that produced a graded row.
   *
   * NARROWED FROM "somebody in it AND something gradeable" TO JUST THE FIRST HALF, and the
   * reason is a measurement rather than a preference. The second half was read off
   * `section_breakdown.length`, and the academy-wide endpoint deliberately does not compute
   * the breakdown: doing so costs `CourseGrade.summary` per learner, which took 31.69s
   * against 2.60s without it over the same 37 enrolments. Keeping the old test against an
   * always-empty list would have filtered EVERY course out of this picker — a dead control
   * rather than a slow one.
   *
   * What is lost is the "· N assignments" hint and the ability to hide a course that grades
   * nothing at all. What replaces it is the honest signal the endpoint does return: a course
   * with graded rows is offered, and opening it computes the real breakdown, which is where
   * "this course grades nothing" is now discovered — one screen later, and correct.
   */
  const choices = useMemo(() => {
    const graded = new Map<string, number>();
    for (const row of all) graded.set(row.course_id, (graded.get(row.course_id) ?? 0) + 1);
    return courseChoices(
      courses.all,
      (entry) => {
        const learners = graded.get(entry.course_key);
        if (!learners) return null;
        return `${learners} graded`;
      },
      course,
    );
  }, [all, courses.all, course]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all
      .filter((row) => (band === 'all' ? true : band === 'passing' ? row.passed : !row.passed))
      .filter((row) => (course ? row.course_id === course : true))
      .filter((row) =>
        term ? row.username.toLowerCase().includes(term) || row.course_title.toLowerCase().includes(term) : true,
      )
      // Highest first. Inside "Not passing" that puts the nearly-there learners at the top,
      // which is the list somebody acts on; inside "Passing" it is who is ready to be
      // certified. Ties break on the learner's name so the order is stable between renders.
      .sort((a, b) => b.current_grade - a.current_grade || a.username.localeCompare(b.username));
  }, [all, band, course, search, byKey]);

  /**
   * The MIDDLE grade, not the mean.
   *
   * An academy where most enrolments have not been opened yet is mostly zeroes, and an average
   * of those says nothing about the people who are working. The median at least names a real
   * learner's position, and when it is 0% that is itself the finding.
   */
  const median = useMemo(() => {
    if (all.length === 0) return null;
    const sorted = all.map((row) => row.current_grade).sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    const upper = sorted[middle] ?? 0;
    const lower = sorted[middle - 1] ?? upper;
    return sorted.length % 2 === 1 ? upper : Math.round((lower + upper) / 2);
  }, [all]);

  const untouched = all.filter((row) => row.current_grade === 0).length;

  const columns: Column<AcademyGrade>[] = [
    { key: 'learner', header: 'Learner', render: (row) => row.username },
    // The course column earns its place only while the table spans courses: with one chosen it
    // is the same name on every row, and the panels below already say which course this is.
    ...(course
      ? []
      : [
          {
            key: 'course',
            header: 'Course',
            render: (row: AcademyGrade) => (
              <CourseCell course={byKey.get(row.course_id)} courseId={row.course_id} />
            ),
          },
        ]),
    {
      key: 'grade',
      header: 'Grade',
      numeric: true,
      render: (row) => (
        <>
          {row.current_grade}%
          {/* Which assignments they have actually scored on, since a bare percentage cannot tell
              "has not started" from "did badly". `reason` is new: the endpoint keeps a row the
              old per-course fan-out could not, for an enrolment the platform simply cannot grade
              at all, and names why rather than showing a silent zero.

              GATED ON `has_breakdown`, NOT ON THE LIST BEING EMPTY. On this academy-wide table
              the breakdown is deliberately not computed (see `academy.ts::AcademyGrade`), so an
              empty list here means "not asked for" — printing "nothing graded in this course"
              from it would state the opposite of the truth for every row. The sentence is shown
              only where the breakdown is real, which is the per-course drill-in. */}
          <small className="admin-sub">
            {row.reason ??
              (!row.has_breakdown
                ? null
                : row.section_breakdown.length === 0
                  ? 'nothing graded in this course'
                  : `${row.section_breakdown.filter((entry) => entry.percent > 0).length} of ${row.section_breakdown.length} scored`)}
          </small>
        </>
      ),
    },
    {
      key: 'passing',
      header: 'Standing',
      render: (row) => (
        <Pill tone={row.passed ? 'active' : 'neutral'}>{row.passed ? 'Passing' : 'Not yet'}</Pill>
      ),
    },
  ];

  const loading = courses.isPending || grades.isPending;
  const chosen = byKey.get(course);

  return (
    <>
      <PageHeader
        title="Grades"
        description="How everyone in this academy is scoring, and every gradebook behind it."
      />

      <div className="admin-stack">
        <KpiRow>
          <Kpi
            label="Learners graded"
            value={loading ? '—' : new Set(all.map((row) => row.username)).size}
            hint={all.length ? `${all.length} enrolments graded` : undefined}
          />
          <Kpi
            label="Passing"
            value={loading ? '—' : passing.length}
            hint={all.length ? `of ${all.length}, by each course's own cutoff` : undefined}
          />
          <Kpi
            label="Median grade"
            value={median === null ? '—' : `${median}%`}
            hint={median === 0 ? 'half have scored nothing yet' : 'half are above this'}
          />
          <Kpi
            label="Nothing scored yet"
            value={loading ? '—' : untouched}
            hint={untouched > 0 ? 'no graded work attempted' : undefined}
          />
        </KpiRow>

        <section className="admin-panel">
          <Toolbar>
            <Tabs
              label="Grade standing"
              active={band}
              onChange={(id) => setBand(id as Band)}
              tabs={[
                { id: 'all', label: 'Everyone', count: loading ? undefined : all.length },
                { id: 'passing', label: 'Passing', count: loading ? undefined : passing.length },
                {
                  id: 'not-passing',
                  label: 'Not yet',
                  count: loading ? undefined : all.length - passing.length,
                },
              ]}
            />

            <CourseSwitcher
              choices={choices}
              value={course}
              onChange={setCourse}
              allMeasure={
                loading
                  ? 'Loading…'
                  : `${choices.length} course${choices.length === 1 ? '' : 's'} with grades`
              }
              hiddenNote={
                courses.all.length > choices.length
                  ? `${courses.all.length - choices.length} of this academy's ${courses.all.length} courses have nobody graded or nothing gradeable, and are not listed.`
                  : undefined
              }
              disabled={loading}
              searchPlaceholder="Search courses with grades…"
            />

            <div className="toolbar__end">
              <SearchField value={search} onChange={setSearch} placeholder="Search learner or course" />
            </div>
          </Toolbar>

          {(courses.isError || grades.isError) && (
            <Banner tone="danger">
              <strong>We could not load the grades.</strong> Nothing has changed. Reload the page
              to try again — this needs staff access to the academy's courses.
            </Banner>
          )}

          {loading ? (
            <Spinner label="Loading grades…" />
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(row) => `${row.username}|${row.course_id}`}
              empty={
                all.length === 0
                  ? 'Nobody has a grade in this academy yet.'
                  : 'No grade matches these filters.'
              }
            />
          )}

          {/*
            The old three banners here (skipped courses, partial courses, courses that failed)
            were each a symptom of fanning out one request per course — a course could be left
            out, cut short, or individually refused. Paging by enrolment removes all three
            failure modes at once: the only way this list is incomplete now is the ordinary
            "there is another page", which `count` reports precisely.
          */}
          {grades.data && grades.data.count > grades.data.results.length && (
            <Banner tone="info">
              <strong>
                Showing {grades.data.results.length} of {grades.data.count} graded enrolments.
              </strong>{' '}
              Choose a course to see its whole gradebook, or generate a grade report below.
            </Banner>
          )}
        </section>

        {course ? (
          <CourseGradebook courseId={course} courseName={chosen?.display_name ?? course} />
        ) : (
          <section className="admin-panel">
            <header className="admin-panel__head">
              <h2>Gradebook, policy and reports</h2>
            </header>
            <p className="admin-note">
              A gradebook belongs to a course: its columns are that course's assignments, and its
              weightings are that course's policy. Choose a course above to see the marks
              assignment by assignment and to generate a grade report.
            </p>
          </section>
        )}
      </div>
    </>
  );
}

/** One course's gradebook, its grading policy, and its reports. */
function CourseGradebook({ courseId, courseName }: { courseId: string; courseName: string }) {
  const [search, setSearch] = useState('');

  const grading = useQuery({
    queryKey: ['grading-info', courseId],
    queryFn: () => getGradingInfo(courseId),
  });

  const gradebook = useQuery({
    queryKey: ['gradebook', courseId, search],
    queryFn: () => listGradebook(courseId, { search: search.trim() || undefined }),
  });

  /**
   * The assignment columns, taken from the FIRST row's breakdown.
   *
   * Every learner's breakdown covers the same subsections in the same order — it is generated
   * from the course's grading policy, not from what they happened to attempt — so one row
   * defines the columns. Reading them from `grading-info.subsections` instead would look tidier
   * and be wrong: that list includes subsections the gradebook does not score.
   */
  const assignments = useMemo(() => {
    const first = gradebook.data?.results?.[0];
    return (first?.section_breakdown ?? []).map((entry) => ({
      label: entry.label,
      name: entry.subsection_name,
      category: entry.category,
      moduleId: entry.module_id,
    }));
  }, [gradebook.data]);

  const cutoff = useMemo(() => {
    const cutoffs = grading.data?.grade_cutoffs ?? {};
    // The pass mark is the LOWEST cutoff: the policy lists each letter's threshold, and anything
    // at or above the smallest one is a pass.
    const values = Object.values(cutoffs);
    return values.length > 0 ? Math.min(...values) : null;
  }, [grading.data]);

  const columns: Column<GradebookRow>[] = [
    { key: 'learner', header: 'Learner', render: (row) => row.username },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      render: (row) => {
        const percent = asPercent(row.percent) ?? 0;
        const passing = cutoff !== null ? row.percent >= cutoff : null;
        return (
          <>
            {percent}%{' '}
            {passing === null ? null : (
              <Pill tone={passing ? 'active' : 'neutral'}>{passing ? 'Passing' : 'Not yet'}</Pill>
            )}
          </>
        );
      },
    },
    ...assignments.map((assignment) => ({
      key: assignment.moduleId,
      header: assignment.label,
      numeric: true,
      render: (row: GradebookRow) => {
        const score = row.section_breakdown.find((entry) => entry.module_id === assignment.moduleId);
        if (!score) return '—';
        // An unattempted assignment is not a zero — it is nothing yet, and printing 0% beside a
        // learner who has not opened it reads as a failure they have not earned.
        if (!score.attempted) return <span className="admin-muted">—</span>;
        return `${score.score_earned}/${score.score_possible}`;
      },
    })),
  ];

  return (
    <>
      {grading.data && (
        <section className="admin-panel">
          <header className="admin-panel__head">
            <h2>Grading policy · {courseName}</h2>
          </header>
          <div className="admin-facts">
            {Object.entries(grading.data.assignment_types).map(([name, type]) => (
              <div key={name}>
                <i>{name}</i>
                <b>{Math.round((type.weight ?? 0) * 100)}%</b>
                <small>
                  {type.min_count} assignment{type.min_count === 1 ? '' : 's'}
                  {type.drop_count > 0 ? `, lowest ${type.drop_count} dropped` : ''}
                </small>
              </div>
            ))}
            {Object.entries(grading.data.grade_cutoffs).map(([letter, value]) => (
              <div key={letter}>
                <i>{letter} from</i>
                <b>{Math.round(value * 100)}%</b>
              </div>
            ))}
          </div>
          {grading.data.grades_frozen && (
            <Banner tone="info">
              <strong>Grades are frozen for this course.</strong> The course ended long enough ago
              that the platform no longer recalculates them.
            </Banner>
          )}
        </section>
      )}

      <section className="admin-panel">
        <header className="admin-panel__head">
          <h2>
            Assignment by assignment{' '}
            {gradebook.data ? (
              <span className="admin-count">{gradebook.data.filtered_users_count}</span>
            ) : null}
          </h2>
          <div className="toolbar__end">
            <SearchField value={search} onChange={setSearch} placeholder="Search username or email" />
          </div>
        </header>

        {gradebook.isError && (
          <Banner tone="danger">
            <strong>We could not load the gradebook.</strong> Reload to try again — this needs
            staff access to the course.
          </Banner>
        )}

        {gradebook.isPending ? (
          <Spinner label="Loading grades…" />
        ) : (
          <DataTable
            columns={columns}
            rows={gradebook.data?.results ?? []}
            rowKey={(row) => String(row.user_id)}
            empty={
              search
                ? 'No learner matches that search.'
                : 'No grades yet — nobody has been graded in this course.'
            }
          />
        )}

        {gradebook.data?.next && (
          <p className="admin-note">
            Showing {gradebook.data.results.length} of {gradebook.data.total_users_count} learners.
            Search for someone specific, or generate a grade report below for the full set.
          </p>
        )}
      </section>

      <Reports courseId={courseId} />
    </>
  );
}

/**
 * The reports panel.
 *
 * WHAT WAS WRONG WITH THE ONE THIS REPLACES, and it is worth stating because the screen looked
 * finished: it hard-coded `grade_report` and `problem_grade_report`, which the platform's
 * `ReportType` does not contain (they are `grade` and `problem_grade`), so every click 400'd;
 * it read `file.name` and `file.url` from a payload whose fields are `report_name` and
 * `report_url`, so a link would have rendered blank even if a file had ever existed; and it
 * rendered an empty list identically for "nobody has generated one", "one is running", "the
 * last one failed" and "the file exists and the server cannot see it".
 *
 * THREE RULES THIS PANEL IS WRITTEN TO, all of them about not showing a permissive default:
 *
 *   1. THE BUTTONS COME FROM THE SERVER. `available` is derived from the platform's own enum.
 *      If it is absent the panel says so and renders NO buttons — a guessed identifier is
 *      exactly the bug being fixed, and a button that 400s is worse than no button.
 *   2. `store.status !== 'ok'` IS A FAULT, including when `store` is missing entirely. The
 *      empty list is only ever shown under an explicit `ok`.
 *   3. A RUNNING JOB IS SHOWN AS RUNNING. Generation is asynchronous, so the answer to
 *      "I clicked Generate and nothing happened" has to be on the screen, and so does a
 *      failure — with whatever the worker said about it.
 */
function Reports({ courseId }: { courseId: string }) {
  const queryClient = useQueryClient();

  const reports = useQuery({
    queryKey: ['reports', courseId],
    queryFn: () => listReports(courseId),
    // A report appears when its background job finishes, so this polls while the page is open.
    refetchInterval: 15_000,
  });

  const ask = useMutation({
    mutationFn: (reportType: string) => generateReport(courseId, reportType),
    onSuccess: () => {
      // Refetch rather than optimistically inserting a row: the job's real state comes from
      // `InstructorTask`, and inventing a "running" row here would be a second source of truth
      // that could outlive a job that failed immediately.
      queryClient.invalidateQueries({ queryKey: ['reports', courseId] });
    },
  });

  const payload = reports.data;
  const available = payload?.available;
  const store = payload?.store;
  // NOT `store?.status !== 'ok'` folded into the render: written once, here, so there is a
  // single place that decides what counts as healthy. An absent `store` is a fault.
  const storeIsHealthy = store?.status === 'ok';
  const files = payload?.results ?? [];
  const running = payload?.generating ?? [];
  const failed = payload?.failed ?? [];

  return (
    <section className="admin-panel">
      <header className="admin-panel__head">
        <h2>Reports</h2>
      </header>

      {reports.isError && (
        <Banner tone="danger">
          <strong>The reports could not be loaded.</strong> This is not "there are none" — the
          request itself failed, so nothing here can be trusted. Reload the page; if it keeps
          happening the academy's server is refusing the request.
        </Banner>
      )}

      {reports.isSuccess && !available && (
        <Banner tone="danger">
          <strong>The academy did not say which reports it can produce.</strong> No buttons are
          shown, because the only alternative is guessing an identifier — and a guessed
          identifier is exactly what stopped every report on this deployment from ever being
          generated. This needs a look at the server.
        </Banner>
      )}

      {available && available.length > 0 && (
        <div className="admin-actions">
          {available.map((report, index) => (
            <Button
              key={report.report_type}
              // The first report the server lists is the primary action. Taken from the map
              // index rather than by comparing against `available[0]`, which under
              // `noUncheckedIndexedAccess` is legitimately `undefined` — an empty array cannot
              // reach here, but the compiler is right that nothing in the expression said so.
              variant={index === 0 ? 'primary' : 'secondary'}
              busy={ask.isPending && ask.variables === report.report_type}
              title={report.description}
              onClick={() => ask.mutate(report.report_type)}
            >
              Generate {report.label.toLowerCase()}
            </Button>
          ))}
        </div>
      )}

      {ask.isError && (
        <Banner tone="danger">
          <strong>The academy would not start that report.</strong>{' '}
          {ask.error instanceof ApiError && typeof ask.error.body === 'object' && ask.error.body
            ? String((ask.error.body as { error?: string }).error ?? ask.error.message)
            : String((ask.error as Error)?.message ?? 'No reason was given.')}{' '}
          Nothing was generated.
        </Banner>
      )}

      {ask.isSuccess && running.length === 0 && failed.length === 0 && (
        <Banner tone="info">
          <strong>Asked for.</strong> {ask.data?.status ?? 'The report is being created.'} It runs
          in the background and appears below when it finishes; this page refreshes itself.
        </Banner>
      )}

      {running.length > 0 && (
        <Banner tone="info">
          <strong>
            {running.length === 1
              ? 'A report is being generated now.'
              : `${running.length} reports are being generated now.`}
          </strong>{' '}
          Usually under a minute, longer for a large course. This page refreshes itself, and the
          file appears in the list below when the job finishes.
          <ul className="admin-files">
            {running.map((task) => (
              <li key={task.task_id}>
                {task.report_type} — {task.state.toLowerCase()}
                {task.requested_at ? `, asked for at ${task.requested_at}` : ''}
              </li>
            ))}
          </ul>
        </Banner>
      )}

      {failed.length > 0 && (
        <Banner tone="danger">
          <strong>
            {failed.length === 1 ? 'A report failed.' : `${failed.length} reports failed.`}
          </strong>{' '}
          No file was produced. Asking again is safe; if it keeps failing, the detail below is
          what the worker said.
          <ul className="admin-files">
            {failed.map((task) => (
              <li key={task.task_id}>
                {task.report_type} — {task.state.toLowerCase()}
                {task.detail ? `: ${task.detail}` : ' (the worker gave no detail)'}
              </li>
            ))}
          </ul>
        </Banner>
      )}

      {reports.isSuccess && !storeIsHealthy && (
        <Banner tone="danger">
          <strong>Reports cannot be read from here.</strong>{' '}
          {store?.detail ??
            'The academy did not report on the health of its report store, which is not the same as it being empty.'}{' '}
          This is a fault, not an empty list: any report that has been generated still exists and
          this screen cannot reach it. Nothing an administrator does on this page will fix it.
        </Banner>
      )}

      {reports.isPending ? (
        <Spinner label="Loading reports…" />
      ) : (
        <ul className="admin-files">
          {/*
            The empty line is shown ONLY under a healthy store. Under a fault the banner above
            has already said what is wrong, and repeating "no reports yet" underneath it would
            put the exact sentence that hid this bug back on the screen.
          */}
          {files.length === 0 && storeIsHealthy && running.length === 0 && (
            <li className="admin-muted">No reports have been generated for this course yet.</li>
          )}
          {files.map((file) => (
            <li key={file.name}>
              {/*
                A plain link, followed rather than fetched: the response is a
                `Content-Disposition: attachment` stream from our own tenant-scoped endpoint, so
                the browser saves it and the session cookie goes with it. `download` is
                deliberately NOT set — the server names the file, and letting the attribute
                rename it would hide which course and timestamp a CSV on someone's desktop
                belongs to.
              */}
              <a href={reportDownloadUrl(courseId, file.name)}>{file.name}</a>
              {file.generated_at ? ` — ${file.generated_at}` : ' — generated at an unknown time'}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
